/**
 * alignement — le calcul d'alignement des calques, en fonction PURE.
 *
 * Sorti de `useLabelStore` pour pouvoir être éprouvé sans navigateur ni magasin
 * (cf. `banc/banc-alignement.mjs`) : le placement d'un calque au millimètre est
 * exactement le genre de chose qu'on croit juste en la lisant et qu'on livre
 * fausse. Le magasin ne fait plus qu'appliquer le résultat.
 */
import { descendantIds, computeGroupBBox } from './layerTree';

const HORIZONTAUX = new Set(['left', 'center', 'right']);
const VERTICAUX = new Set(['top', 'middle', 'bottom']);

/** L'unité de placement de toute l'application. */
const auDixieme = (v) => Math.round(v * 10) / 10;

/**
 * Enveloppe d'une cible d'alignement, et les feuilles qui doivent la suivre.
 *
 * Un GROUPE n'a pas de x/y à lui : sa géométrie est l'enveloppe de ses feuilles.
 * Écrire `x` sur le nœud de groupe ne déplaçait donc rien, ni à l'écran ni à
 * l'export — d'où le passage par un décalage appliqué aux feuilles.
 */
function enveloppeDe(elements, el) {
    if (el.type === 'group') {
        const feuilles = descendantIds(elements, el.id).filter((id) => {
            const d = elements.find((x) => x.id === id);
            return d && d.type !== 'group';
        });
        return { boite: computeGroupBBox(elements, el.id), feuilles };
    }
    return {
        boite: { x: el.x, y: el.y, width: el.width || 0, height: el.height || 0 },
        feuilles: [el.id],
    };
}

/**
 * Calcule les nouvelles positions d'un alignement.
 *
 * La RÉFÉRENCE dépend du nombre de cibles :
 *   - plusieurs → elles s'alignent ENTRE ELLES, sur l'enveloppe de la sélection ;
 *   - une seule → elle s'aligne sur LE CADRE DE L'ÉTIQUETTE (le format fini).
 *     C'était refusé purement et simplement, si bien que centrer un objet seul —
 *     le geste le plus courant d'Illustrator — n'existait pas : il fallait viser
 *     à la main ou calculer la position dans l'inspecteur. Relevé à l'essai
 *     atelier de septembre, sur le tracé de découpe précisément.
 *
 * Les coordonnées des calques sont comptées depuis le bord du FORMAT FINI, le
 * fond perdu vivant en dehors (cf. `el.x + bleed` au rendu) : les bornes du cadre
 * sont donc 0 et la dimension du canevas, sans correction.
 *
 * @param {Array}  elements  tous les calques du document
 * @param {string[]} ids     les calques sélectionnés
 * @param {string} direction left|center|right|top|middle|bottom
 * @param {{width_mm:number, height_mm:number}} canvas
 * @returns {Array|null} le nouveau tableau de calques, ou null si rien ne bouge
 */
export function calculerAlignement(elements, ids, direction, canvas) {
    const axeX = HORIZONTAUX.has(direction);
    if (!axeX && !VERTICAUX.has(direction)) return null;
    if (!Array.isArray(elements) || !Array.isArray(ids) || ids.length === 0) return null;

    const cibles = elements
        .filter((el) => ids.includes(el.id) && !el.locked && el.type !== 'guide')
        .map((el) => enveloppeDe(elements, el))
        .filter((c) => c.feuilles.length > 0);
    if (cibles.length === 0) return null;

    const debut = (c) => (axeX ? c.boite.x : c.boite.y);
    const taille = (c) => (axeX ? c.boite.width : c.boite.height) || 0;
    const fin = (c) => debut(c) + taille(c);

    let refDebut, refFin;
    if (cibles.length >= 2) {
        refDebut = Math.min(...cibles.map(debut));
        refFin = Math.max(...cibles.map(fin));
    } else {
        refDebut = 0;
        refFin = Number(axeX ? canvas?.width_mm : canvas?.height_mm) || 0;
        if (refFin <= 0) return null;
    }

    // Position visée pour le DÉBUT de l'enveloppe de chaque cible.
    const viser = (c) => {
        switch (direction) {
            case 'left': case 'top':     return refDebut;
            case 'right': case 'bottom': return refFin - taille(c);
            default:                     return (refDebut + refFin) / 2 - taille(c) / 2;
        }
    };

    // Un décalage par feuille : les enfants d'un groupe gardent leurs écarts.
    const decalages = new Map();
    for (const c of cibles) {
        const d = auDixieme(viser(c)) - debut(c);
        if (d === 0) continue;
        for (const id of c.feuilles) decalages.set(id, d);
    }
    if (decalages.size === 0) return null;

    return elements.map((el) => {
        const d = decalages.get(el.id);
        if (d === undefined) return el;
        return axeX ? { ...el, x: auDixieme(el.x + d) } : { ...el, y: auDixieme(el.y + d) };
    });
}
